import apiClient from './api';
import type { Role, Permission, PaginatedResponse, PaginationParams } from '@/types';

export const rbacService = {
  /**
   * Get all roles
   */
  async getRoles(params?: PaginationParams): Promise<PaginatedResponse<Role>> {
    const { data } = await apiClient.get<PaginatedResponse<Role>>('/rbac/roles', { params });
    return data;
  },

  /**
   * Get single role by ID
   */
  async getRole(id: string): Promise<Role> {
    const { data } = await apiClient.get<Role>(`/rbac/roles/${id}`);
    return data;
  },

  /**
   * Create new role
   */
  async createRole(roleData: Partial<Role>): Promise<Role> {
    const { data } = await apiClient.post<Role>('/rbac/roles', roleData);
    return data;
  },

  /**
   * Update role
   */
  async updateRole(id: string, roleData: Partial<Role>): Promise<Role> {
    const { data } = await apiClient.put<Role>(`/rbac/roles/${id}`, roleData);
    return data;
  },

  /**
   * Delete role
   */
  async deleteRole(id: string): Promise<void> {
    await apiClient.delete(`/rbac/roles/${id}`);
  },

  /**
   * Get all permissions
   */
  async getPermissions(params?: PaginationParams): Promise<PaginatedResponse<Permission>> {
    const { data } = await apiClient.get<PaginatedResponse<Permission>>('/rbac/permissions', { params });
    return data;
  },

  /**
   * Create permission
   */
  async createPermission(permissionData: Partial<Permission>): Promise<Permission> {
    const { data } = await apiClient.post<Permission>('/rbac/permissions', permissionData);
    return data;
  },

  /**
   * Delete permission
   */
  async deletePermission(id: string): Promise<void> {
    await apiClient.delete(`/rbac/permissions/${id}`);
  },

  /**
   * Get user roles
   */
  async getUserRoles(userId: string): Promise<Role[]> {
    const { data } = await apiClient.get<Role[]>(`/rbac/users/${userId}/roles`);
    return data;
  },

  /**
   * Assign roles to user
   */
  async assignUserRoles(userId: string, roleIds: string[]): Promise<void> {
    await apiClient.post(`/rbac/users/${userId}/roles`, { role_ids: roleIds });
  },

  /**
   * Seed default roles and permissions
   */
  async seedDefaults(): Promise<{ message: string }> {
    const { data } = await apiClient.post('/rbac/seed');
    return data;
  },
};
